// @ts-nocheck
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { CarouselInspirationBorrow } from "@/lib/api";
import type { BgMode } from "@/lib/carouselStudioMeta";
import { slideHasCopy } from "../helpers";

export function useCarouselMutations(ctx: any) {
  const {
    selectedId,
    qc,
    DEFAULT_BORROW,
    activeMode,
    setSelectedId,
    setTraceId,
    setForcePick,
    setComposeMode,
    briefBody,
    setCaption,
    setCopySlides,
    setBorrow,
    traceId,
    copyFeedback,
    draftText,
    draftEditMode,
    captionFeedback,
    verifyCta,
    item,
    copySlides,
    mergeCopySlides,
    copyMdRef,
    bgHint,
    caption,
    when,
    setFinalPreview,
    goTab,
    setStyleId,
    setVisualPackId,
    setFormatId,
    setBgMode,
    isOperator,
    publishChannel,
    fbPublishMode
  } = ctx;
  const searchMusicM = useMutation({ mutationFn: (q: string) => api.carouselMusicSearch(q) });
  const reelM = useMutation({
    mutationFn: (trackId: string) => api.carouselComposeReel(selectedId!, trackId),
    onSuccess: (data) => {
      qc.setQueryData(["carousel", selectedId], data);
      qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
    },
  });

  const createM = useMutation({
      mutationFn: (kind: "carousel" | "feed_post" | "czasotekst" = "carousel") =>
        api.carouselCreate(
          briefBody(
            kind === "carousel" ? (activeMode === "operator" ? "operator" : "ai") : "ai",
            kind,
          ),
        ),
      onSuccess: (res) => {
        setSelectedId(res.item.id);
        setTraceId(res.item.id);
        setForcePick(false);
        if (res.item.compose_mode === "operator" || res.item.compose_mode === "ai") {
          setComposeMode(res.item.compose_mode);
        }
        qc.setQueryData(["carousel", res.item.id], res.item);
        qc.invalidateQueries({ queryKey: ["carousels"] });
        if (res.kind === "czasotekst" || res.item?.kind === "czasotekst") {
          goTab("copy");
        }
      },
    });

  const ctaM = useMutation({
      mutationFn: (body: { cta_intent?: string; close_mode?: string; ab_variable?: string; ab_pair_id?: string }) =>
        api.carouselPatchCta(selectedId!, body),
      onSuccess: (res) => {
        qc.setQueryData(["carousel", res.id], res);
      },
    });

  const borrowM = useMutation({
      mutationFn: (next: CarouselInspirationBorrow) => api.carouselInspirationBorrow(selectedId!, next),
      onSuccess: (res) => {
        qc.setQueryData(["carousel", res.id], res);
        setBorrow({ ...DEFAULT_BORROW, ...res.inspiration_borrow });
      },
    });

  const analyzeInspM = useMutation({
      mutationFn: (opts?: { regenerate?: boolean; force_fetch?: boolean }) =>
        api.carouselInspirationAnalyze(selectedId!, opts),
      onSuccess: (res) => {
        qc.setQueryData(["carousel", res.id], res);
        qc.invalidateQueries({ queryKey: ["carousel", res.id] });
      },
    });

  const uploadInspM = useMutation({
      mutationFn: (file: File) => api.carouselUploadInspiration(selectedId!, file),
      onSuccess: (res) => {
        qc.setQueryData(["carousel", res.id], res);
      },
    });

  const abortLlmM = useMutation({
      mutationFn: () => api.carouselCancelLlm((traceId || selectedId)!),
      onSuccess: (res) => {
        const id = res.id || traceId || selectedId;
        if (id) qc.setQueryData(["carousel", id], res);
        qc.invalidateQueries({ queryKey: ["carousels"] });
      },
    });

  const selectM = useMutation({
      mutationFn: (ideaId: string) => api.carouselSelectIdea(selectedId!, ideaId),
      onSuccess: (res) => {
        setCaption(res.caption || "");
        setCopySlides([]);
        qc.setQueryData(["carousel", selectedId], res);
        qc.invalidateQueries({ queryKey: ["carousels"] });
      },
    });

  const generateCopyM = useMutation({
      mutationFn: () => api.carouselGenerateCopy(selectedId!, copyFeedback),
      onSuccess: (res) => {
        const slides = res.copy?.slides || [];
        if (slides.length) {
          setCopySlides(slides.map((slide: Record<string, unknown>) => ({ ...slide })));
        }
        if (res.caption != null) setCaption(res.caption || "");
        qc.setQueryData(["carousel", selectedId], res);
        qc.invalidateQueries({ queryKey: ["carousels"] });
      },
    });

  const reviseCopyM = useMutation({
      mutationFn: () => api.carouselGenerateCopyRevise(selectedId!, copyFeedback),
      onSuccess: (res) => {
        const slides = res.copy?.slides || [];
        if (slides.length) {
          setCopySlides(slides.map((slide: Record<string, unknown>) => ({ ...slide })));
        }
        if (res.caption != null) setCaption(res.caption || "");
        qc.setQueryData(["carousel", selectedId], res);
        qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
        qc.invalidateQueries({ queryKey: ["carousels"] });
      },
    });

  const applyCopyReviewM = useMutation({
      mutationFn: (actionIds: string[]) => api.carouselApplyCopyReview(selectedId!, actionIds),
      onSuccess: (res) => {
        const slides = res.copy?.slides || [];
        if (slides.length) {
          setCopySlides(
            slides.map((s: any) => ({
              type: s.type || "content",
              text: s.text || s.heading || "",
              sub: s.sub || s.body || "",
              recipe: s.recipe || "",
              emphasis: s.emphasis || "",
            })),
          );
        }
        if (res.caption != null) setCaption(res.caption || "");
        qc.setQueryData(["carousel", selectedId], res);
        qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
        qc.invalidateQueries({ queryKey: ["carousels"] });
      },
    });

  const fromDraftM = useMutation({
      mutationFn: async () => {
        if (selectedId) return api.carouselFromDraft(selectedId, draftText, draftEditMode);
        const created = await api.carouselCreate(briefBody("operator"));
        setSelectedId(created.item.id);
        setComposeMode("operator");
        qc.setQueryData(["carousel", created.item.id], created.item);
        return api.carouselFromDraft(created.item.id, draftText, draftEditMode);
      },
      onSuccess: (res) => {
        setSelectedId(res.id);
        setForcePick(false);
        setComposeMode("operator");
        qc.setQueryData(["carousel", res.id], res);
        qc.invalidateQueries({ queryKey: ["carousels"] });
      },
    });

  const captionM = useMutation({
      mutationFn: () => api.carouselWriteCaption(selectedId!, captionFeedback, verifyCta),
      onSuccess: (res) => {
        setCaption(res.caption || "");
        setCaptionFeedback(res.caption_feedback || captionFeedback);
        qc.setQueryData(["carousel", selectedId], res);
        qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
        qc.invalidateQueries({ queryKey: ["carousels"] });
      },
    });

  const composeModeM = useMutation({
      mutationFn: (mode: "operator" | "ai") => api.carouselComposeMode(selectedId!, mode),
      onSuccess: (res) => {
        setForcePick(false);
        if (res.compose_mode === "operator" || res.compose_mode === "ai") setComposeMode(res.compose_mode);
        setCopySlides([]);
        setCaption("");
        setCaptionFeedback("");
        qc.setQueryData(["carousel", res.id], res);
        qc.invalidateQueries({ queryKey: ["carousels"] });
      },
    });

  const patchCopyM = useMutation({
      mutationFn: (slides: Record<string, unknown>[]) => {
        if (!selectedId) throw new Error("brak karuzeli");
        return api.carouselPatchCopy(selectedId, {
          copy: { ...(item?.copy || {}), slides },
        });
      },
      onSuccess: (res) => {
        qc.setQueryData(["carousel", selectedId], res);
        const slides = res.copy?.slides;
        if (Array.isArray(slides)) {
          copyMdRef.current = {};
          setCopySlides(slides.map((slide) => ({ ...slide })));
        }
      },
    });

  const renderM = useMutation({
      mutationFn: async (fallback: boolean) => {
        if (!selectedId) throw new Error("brak karuzeli");
        const savedSlides = copySlides.some((slide) => slideHasCopy(slide))
          ? mergeCopySlides()
          : item?.copy?.slides || copySlides;
        await api.carouselPatchCopy(selectedId, {
          copy: { ...(item?.copy || {}), slides: savedSlides },
          caption,
        });
        await api.carouselPatchTemplate(selectedId, { bg_hint: bgHint });
        return api.carouselApproveCopy(selectedId, fallback);
      },
      onSuccess: () => qc.invalidateQueries({ queryKey: ["carousel", selectedId] }),
    });

  const cancelRenderM = useMutation({
      mutationFn: () => api.carouselCancelRender(selectedId!),
      onSuccess: () => qc.invalidateQueries({ queryKey: ["carousel", selectedId] }),
    });

  const approveM = useMutation({
      mutationFn: async () => {
        const approved = await api.carouselApprove(selectedId!);
        if (when) {
          await api.carouselSchedule(selectedId!, { scheduled_at: when, publish_now: false });
        }
        return approved;
      },
      onSuccess: () => {
        setFinalPreview(null);
        qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
        qc.invalidateQueries({ queryKey: ["carousels"] });
        qc.invalidateQueries({ queryKey: ["carousel-publish-slot"] });
        goTab("publish");
      },
    });

  const recomposeM = useMutation({
      mutationFn: () => api.carouselBake(selectedId!),
      onSuccess: (data) => {
        qc.setQueryData(["carousel", selectedId], data);
        qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
      },
    });

  const layoutQaM = useMutation({
      mutationFn: () => api.carouselLayoutQa(selectedId!),
      onSuccess: (data) => {
        qc.setQueryData(["carousel", selectedId], data);
        qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
      },
    });

  const designReviewM = useMutation({
      mutationFn: () => api.carouselDesignReview(selectedId!),
      onSuccess: (data) => {
        qc.setQueryData(["carousel", selectedId], data);
        qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
      },
    });

  const designFeedbackM = useMutation({
      mutationFn: (body: { rating?: string; text?: string }) => api.carouselDesignFeedback(selectedId!, body),
      onSuccess: (data) => {
        qc.setQueryData(["carousel", selectedId], data);
        qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
      },
    });

  const designApplyM = useMutation({
      mutationFn: (selected?: number[]) => api.carouselDesignApply(selectedId!, selected),
      onSuccess: (data) => {
        qc.setQueryData(["carousel", selectedId], data);
        qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
      },
    });

  const designFixM = useMutation({
      mutationFn: () => api.carouselHouseFix(selectedId!),
      onSuccess: (data) => {
        qc.setQueryData(["carousel", selectedId], data);
        qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
      },
    });

  const scheduleM = useMutation({
      mutationFn: (publishNow: boolean) =>
        api.carouselSchedule(selectedId!, {
          scheduled_at: when,
          publish_now: publishNow,
          channel: publishChannel,
          fb_format: fbPublishMode,
          track_id: item?.reel_track_id || undefined,
        }),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
        qc.invalidateQueries({ queryKey: ["carousels"] });
        qc.invalidateQueries({ queryKey: ["carousel-publish-slot"] });
      },
    });

  const republishM = useMutation({
    mutationFn: (opts: { channel: "ig" | "fb" | "both"; fb_format?: string }) =>
      api.carouselRepublish(selectedId!, {
        channel: opts.channel,
        fb_format: opts.fb_format || fbPublishMode,
        track_id: item?.reel_track_id || undefined,
        publish_now: true,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
      qc.invalidateQueries({ queryKey: ["carousels"] });
    },
  });

  const publishPlatformM = useMutation({
    mutationFn: (opts: { channel: "ig" | "fb" | "both"; force?: boolean }) => {
      const payload = {
        channel: opts.channel,
        fb_format: fbPublishMode,
        track_id: item?.reel_track_id || undefined,
      };
      if (opts.force) {
        return api.carouselRepublish(selectedId!, { ...payload, publish_now: true });
      }
      return api.carouselSchedule(selectedId!, {
        scheduled_at: when,
        publish_now: true,
        force: false,
        ...payload,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
      qc.invalidateQueries({ queryKey: ["carousels"] });
      qc.invalidateQueries({ queryKey: ["carousel-publish-slot"] });
    },
  });

  const retryPublishM = useMutation({
      mutationFn: (vars: { id: string; channel?: "ig" | "fb" | "both" }) =>
        api.carouselRetry(vars.id, {
          channel: vars.channel,
          fb_format: fbPublishMode,
          track_id: item?.reel_track_id || undefined,
        }),
      onSuccess: (_res, vars) => {
        qc.invalidateQueries({ queryKey: ["carousel", vars.id] });
        qc.invalidateQueries({ queryKey: ["carousels"] });
      },
    });

  const fromPubM = useMutation({
      mutationFn: (id: string) => api.carouselFromPublished(id),
      onSuccess: (res) => {
        setSelectedId(res.item.id);
        qc.invalidateQueries({ queryKey: ["carousels"] });
      },
    });

  const pauseM = useMutation({
      mutationFn: (paused: boolean) => api.carouselPause(paused),
      onSuccess: () => qc.invalidateQueries({ queryKey: ["carousel-status"] }),
    });

  const deleteM = useMutation({
      mutationFn: (id: string) => api.carouselDelete(id),
      onSuccess: (_res, id) => {
        if (selectedId === id) {
          setSelectedId(null);
          setCaption("");
          setCopySlides([]);
        }
        qc.removeQueries({ queryKey: ["carousel", id] });
        qc.invalidateQueries({ queryKey: ["carousels"] });
      },
    });

  const ratingM = useMutation({
      mutationFn: ({ id, rating }: { id: string; rating: string }) => api.carouselRating(id, rating),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["carousels"] });
        qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
      },
    });

  const retroM = useMutation({
      mutationFn: (id: string) => api.carouselRetro(id),
      onSuccess: (res) => {
        setSelectedId(res.id);
        qc.invalidateQueries({ queryKey: ["carousels"] });
        qc.invalidateQueries({ queryKey: ["carousel", res.id] });
      },
    });

  const layoutPlanM = useMutation({
      mutationFn: async () => {
        if (selectedId) return api.carouselLayoutPlan(selectedId, draftText, draftEditMode);
        const created = await api.carouselCreate(briefBody(isOperator ? "operator" : "ai"));
        setSelectedId(created.item.id);
        return api.carouselLayoutPlan(created.item.id, draftText, draftEditMode);
      },
      onSuccess: (res) => {
        setSelectedId(res.id);
        qc.setQueryData(["carousel", res.id], res);
        qc.invalidateQueries({ queryKey: ["carousels"] });
      },
    });

  const templateM = useMutation({
      mutationFn: (body: {
        style_id?: string;
        visual_pack_id?: string;
        format_id?: string;
        bg_unique_count?: number;
        bg_mode?: BgMode;
        bg_hint?: string;
        bg_preset_id?: string;
        bg_placement_id?: string;
        layout_preset_id?: string;
        style_apply_mode?: "bg" | "full" | "layout";
      }) => api.carouselPatchTemplate(selectedId!, body),
      onSuccess: (res) => {
        setStyleId(res.style_id || "krem-kasia");
        setVisualPackId?.(res.visual_pack_id || "");
        setFormatId(res.format_id || "");
        setBgMode(res.bg_mode === "gemini" ? "gemini" : "flat");
        qc.invalidateQueries({ queryKey: ["carousel", selectedId] });
      },
    });

  return {
    searchMusicM,
    reelM,
    createM,
    ctaM,
    borrowM,
    analyzeInspM,
    uploadInspM,
    abortLlmM,
    selectM,
    generateCopyM,
    reviseCopyM,
    applyCopyReviewM,
    fromDraftM,
    captionM,
    composeModeM,
    patchCopyM,
    renderM,
    cancelRenderM,
    approveM,
    recomposeM,
    layoutQaM,
    designReviewM,
    designFeedbackM,
    designApplyM,
    designFixM,
    scheduleM,
    republishM,
    publishPlatformM,
    retryPublishM,
    fromPubM,
    pauseM,
    deleteM,
    ratingM,
    retroM,
    layoutPlanM,
    templateM
  };
}
